import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { resumeService } from '../../services/resume';
import { userService } from '../../services/userService';
import { useNavigate } from 'react-router-dom';
import Profile from './Profile';
import ExperienceForm from './forms/ExperienceForm';
import EducationForm from './forms/EducationForm';
import SkillsForm from './forms/SkillsForm';

const steps = ['Create Resume', 'Add Experience', 'Add Education', 'Add Skills'];

const ResumeCreate = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [resumeId, setResumeId] = useState(null);
  const [resumeName, setResumeName] = useState('');
  const [resumeDescription, setResumeDescription] = useState('');

  // Fetch user profile on component mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setProfileLoading(true);
        const profileData = await userService.getProfile();
        setProfile(profileData);
        
        // Pre-fill resume name with user's name
        if (profileData?.fullName) {
          setResumeName(`${profileData.fullName}'s Resume`);
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
        setError('Failed to load profile information. Please try again.');
      } finally {
        setProfileLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleCreateResume = async () => {
    if (!resumeName.trim()) {
      setError('Please enter a resume name.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const resumeData = {
        name: resumeName,
        description: resumeDescription
      };

      const response = await resumeService.createResume(resumeData);
      setResumeId(response.id);
      setSuccess('Resume created successfully! You can now add experience, education, and skills.');
      
      // Move to next step after a short delay
      setTimeout(() => {
        handleNext();
      }, 1500);

    } catch (error) {
      setError('Failed to create resume. Please try again.');
      console.error('Error creating resume:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleError = (errorMessage) => {
    setError(errorMessage);
  };

  const handleFinish = () => {
    // Navigate to the resume detail page
    navigate(`/resumes/${resumeId}`);
  };

  const renderCreateResumeStep = () => (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>Create New Resume</Typography>
      
      {profile && !profileLoading && (
        <Profile profile={profile} loading={profileLoading} />
      )}
      
      <Box sx={{ mt: 3 }}>
        <TextField
          fullWidth
          label="Resume Name"
          value={resumeName}
          onChange={(e) => setResumeName(e.target.value)}
          placeholder="Enter resume name..."
          sx={{ mb: 2 }}
        />
        
        <TextField
          fullWidth
          label="Description (Optional)"
          multiline
          rows={4}
          value={resumeDescription}
          onChange={(e) => setResumeDescription(e.target.value)}
          placeholder="Brief description of this resume..."
        />
      </Box>
    </Paper>
  );

  const renderExperienceStep = () => (
    <ExperienceForm 
      resumeId={resumeId} 
      onError={handleError}
    />
  );

  const renderEducationStep = () => (
    <EducationForm 
      resumeId={resumeId} 
      onError={handleError}
    />
  );

  const renderSkillsStep = () => (
    <SkillsForm 
      resumeId={resumeId} 
      onError={handleError}
    />
  );

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return renderCreateResumeStep();
      case 1:
        return renderExperienceStep();
      case 2:
        return renderEducationStep();
      case 3:
        return renderSkillsStep();
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Create New Resume
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ mb: 4 }}>
        {renderStepContent(activeStep)}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
        >
          Back
        </Button>

        <Box>
          {activeStep === 0 ? (
            <Button
              variant="contained"
              onClick={handleCreateResume}
              disabled={loading || !resumeName.trim() || profileLoading}
              startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              {loading ? 'Creating Resume...' : 'Create Resume'}
            </Button>
          ) : activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleFinish}
              startIcon={<SaveIcon />}
            >
              Finish
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={<ArrowForwardIcon />}
              disabled={!resumeId}
            >
              Next
            </Button>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default ResumeCreate;
